import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  url: string;
  image: string;
  category: string;
  readTime: string;
  publishDate: string;
  slug: string;
  author: string;
  authorSlug: string;
}

const BlogList: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  const decodeHTML = (html: string) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  const getReadingTime = (html: string) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    const text = txt.value.replace(/<[^>]+>/g, "");
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return `${Math.ceil(words / 200)} min read`;
  };

  // ✅ get category slug from URL
  const getCategorySlugFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get("category");
  };

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const categorySlug = getCategorySlugFromQuery();
        let categoryFilter = "";

        // ✅ if category is selected and not 'all', fetch its ID
        if (categorySlug && categorySlug !== "all") {
          const categoryRes = await fetch(
            `https://blog.thecryptotutor.com/wp-json/wp/v2/categories?slug=${categorySlug}`
          );
          const categoryData = await categoryRes.json();
          if (categoryData.length > 0) {
            categoryFilter = `&categories=${categoryData[0].id}`;
          }
        }

        const response = await fetch(
          `https://blog.thecryptotutor.com/wp-json/wp/v2/posts?_embed&per_page=20${categoryFilter}`
        );
        const data = await response.json();

        const mapped = data.map((post: any) => ({
          id: post.id,
          title: decodeHTML(post.title.rendered),
          excerpt: decodeHTML(post.excerpt.rendered).replace(/<[^>]+>/g, "").substring(0, 200) + "...",
          url: post.link,
          image: post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "https://via.placeholder.com/300x200?text=No+Image",
          category: post._embedded?.["wp:term"]?.[0]?.[0]?.name || "Uncategorized",
          readTime: getReadingTime(post.content.rendered),
          publishDate: new Date(post.date).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }),
          slug: post.slug,
          author: post._embedded?.author?.[0]?.name || "Anonymous",
          authorSlug: post._embedded?.author?.[0]?.slug || "unknown",
        }));

        setBlogPosts(mapped);
      } catch (err) {
        console.error("Failed to fetch posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [location.search]); // ✅ refetch whenever URL changes

  if (loading) return <p className="text-center py-10 text-gray-500">Loading posts...</p>;
  if (blogPosts.length === 0) return <p className="text-center py-10 text-gray-500">No posts found.</p>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="space-y-6">
        {blogPosts.map((post) => (
          <article key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col-reverse sm:flex-row gap-4 sm:gap-6">
              <div className="flex-1">
                <div className="space-y-3">
                  <Link to={`/blog/${post.slug}`} className="block">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 hover:text-blue-600 cursor-pointer transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-blue-600 text-sm break-all hover:underline cursor-pointer">{post.url}</p>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{post.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500">
                    <Link to={`/author/${post.authorSlug}`} className="hover:text-blue-600 transition-colors">
                      By {post.author}
                    </Link>
                    <span>•</span>
                    <span>{post.readTime}</span>
                    <span>•</span>
                    <span>{post.publishDate}</span>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-full sm:w-40 h-40 sm:h-32">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover rounded-lg" />
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default BlogList;
